import React, { useEffect, useState } from 'react';

import { FormControlLabel, FormHelperText, Radio, RadioGroup } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { LoadingType, RootState, setLoadingState } from 'actions';
import Loader from 'components/Loader';
import { useAppSelector } from 'hooks/useAppSelector';
import Section from 'components/LoanPortal/Sections/Section';
import { DashboardTabName } from 'enums/DashboardTabName';
import styles from 'pages/Dashboard/steps/InformationTabs/InformationTabs.module.scss';
import * as notificationManager from 'notifications/index';
import { Button } from 'components/Buttons/Button';
import { FailureNotificationType } from 'notifications/index';
import { useAppDispatch } from 'hooks/useAppDispatch';
import {
  validateCityField,
  validateLatinLetters,
} from 'pages/PreQualification/steps/BorrowerInformation/borrowerInformation.utils';
import { convertStringToBoolean, getErrorMessageForRequiredFields } from 'utils/stringUtils';
import Input from 'components/Inputs/Input';
import PhoneNumberInput from 'components/Inputs/PhoneNumberInput';
import {
  contractorInformationApi,
  ContractorInformationResponseDto,
} from 'api/SmallLoanProgram/ContractorInformationApi';
import { validatePhoneNumber } from 'utils/validationUtils';
import states from 'enums/StatesList';
import { formatOptionLabel, OptionType } from 'utils/inputSelect';
import Select from 'components/Select';
import { getApplicationInfoForDashboardThunk } from 'thunks/getApplicationInfoForDashboard';

import { ContractorInformationFormValues } from './contractorInformationTab.types';
import {
  ContractorInfoField,
  ContractorInfoLabel,
  ContractorLocatedRadioValue,
} from './contractorInformationTab.enums';

const stateOptions = [
  ...states.map(({ value }) => ({
    value: value,
    label: value,
  })),
];

const ContractorInformationTab = () => {
  const dispatch = useAppDispatch();

  const loading = useAppSelector(
    (state) => state.loading[`${LoadingType.RequiredInformationUpdating}${DashboardTabName.ContractorInfo}`],
  );
  const isContractorInformationLoading = useAppSelector(
    (state) => state.loading[`${LoadingType.GetData}${DashboardTabName.ContractorInfo}`],
  );
  const { applicationId } = useAppSelector((state) => state.application);
  const { borrowerId } = useSelector((state: RootState) => state.borrower);
  const { contractorInformationComplete } = useAppSelector((state) => state.dashboardPortal);

  const [isContractorInfoOpened, setIsContractorInfoOpened] = useState<boolean>(false);

  const { register, trigger, handleSubmit, watch, formState, setValue, clearErrors, unregister, ...methods } =
    useForm<ContractorInformationFormValues>({
      defaultValues: {
        [ContractorInfoField.ContractorLocated]: null,
        [ContractorInfoField.ContractorBusinessName]: '',
        [ContractorInfoField.ContractorCity]: '',
        [ContractorInfoField.ContractorState]: '',
        [ContractorInfoField.ContractorPhone]: '',
        [ContractorInfoField.ContractorFirstName]: '',
      },
    });

  const watcher = watch();

  const setFormValuesFromDto = (contractorInformation: ContractorInformationResponseDto): void => {
    setValue(ContractorInfoField.ContractorLocated, contractorInformation.contractorIsLocated);
    setValue(ContractorInfoField.ContractorBusinessName, contractorInformation.businessName || '');
    setValue(ContractorInfoField.ContractorCity, contractorInformation.city || '');
    setValue(ContractorInfoField.ContractorState, contractorInformation.state || '');
    setValue(ContractorInfoField.ContractorPhone, contractorInformation.phoneNumber || '');
    setValue(ContractorInfoField.ContractorFirstName, contractorInformation.contactFirstName || '');
  };

  useEffect(() => {
    const getContractorInformation = async () => {
      try {
        dispatch(
          setLoadingState({
            type: `${LoadingType.GetData}${DashboardTabName.ContractorInfo}`,
            loading: true,
          }),
        );

        const response = await contractorInformationApi.get(applicationId!);

        setFormValuesFromDto(response);
        setIsContractorInfoOpened(!!response.contractorIsLocated);
      } catch (err) {
        return notificationManager.notifyFailure(FailureNotificationType.UnknownError);
      } finally {
        dispatch(
          setLoadingState({
            type: `${LoadingType.GetData}${DashboardTabName.ContractorInfo}`,
            loading: false,
          }),
        );
      }
    };

    getContractorInformation();
  }, []);

  useEffect(() => {
    register(ContractorInfoField.ContractorLocated, {
      validate: (value) => value !== null,
    });
  }, []);

  useEffect(() => {
    if (isContractorInfoOpened) {
      register(ContractorInfoField.ContractorBusinessName, {
        required: getErrorMessageForRequiredFields(ContractorInfoLabel.ContractorBusinessName),
      });
      register(ContractorInfoField.ContractorCity, {
        required: getErrorMessageForRequiredFields(ContractorInfoLabel.ContractorCity),
        validate: (value) => {
          return validateCityField(value, ContractorInfoLabel.ContractorCity);
        },
      });
      register(ContractorInfoField.ContractorState, {
        required: getErrorMessageForRequiredFields(ContractorInfoLabel.ContractorState),
      });
      register(ContractorInfoField.ContractorPhone, {
        required: false,
        validate: (value) => {
          return !value ? true : validatePhoneNumber(value);
        },
      });
      register(ContractorInfoField.ContractorFirstName, {
        required: getErrorMessageForRequiredFields(ContractorInfoLabel.ContractorFirstName),
        validate: (value) => {
          return validateLatinLetters(value, ContractorInfoLabel.ContractorFirstName);
        },
      });
    } else {
      unregister([
        ContractorInfoField.ContractorBusinessName,
        ContractorInfoField.ContractorCity,
        ContractorInfoField.ContractorState,
        ContractorInfoField.ContractorPhone,
        ContractorInfoField.ContractorFirstName,
      ]);
    }
  }, [isContractorInfoOpened, contractorInformationComplete]);

  const handleOnSubmit = handleSubmit(async () => {
    const result = await trigger();

    if (result) {
      try {
        dispatch(
          setLoadingState({
            type: `${LoadingType.RequiredInformationUpdating}${DashboardTabName.ContractorInfo}`,
            loading: true,
          }),
        );

        const updateContractorInformationRequestDto = !watcher[ContractorInfoField.ContractorLocated]
          ? {
              applicationId: applicationId!,
              contractorIsLocated: !!watcher[ContractorInfoField.ContractorLocated],
            }
          : {
              applicationId: applicationId!,
              contractorIsLocated: !!watcher[ContractorInfoField.ContractorLocated],
              businessName: watcher[ContractorInfoField.ContractorBusinessName],
              city: watcher[ContractorInfoField.ContractorCity],
              state: watcher[ContractorInfoField.ContractorState],
              contactFirstName: watcher[ContractorInfoField.ContractorFirstName],
              phoneNumber: watcher[ContractorInfoField.ContractorPhone],
            };

        const contractorInformation = await contractorInformationApi.update(updateContractorInformationRequestDto);

        setFormValuesFromDto(contractorInformation);

        await dispatch(getApplicationInfoForDashboardThunk(borrowerId!)).unwrap();
      } catch (error) {
        notificationManager.notifyFailure(FailureNotificationType.UnknownError);
      } finally {
        dispatch(
          setLoadingState({
            type: `${LoadingType.RequiredInformationUpdating}${DashboardTabName.ContractorInfo}`,
            loading: false,
          }),
        );
      }
    }
  });

  return loading || isContractorInformationLoading ? (
    <Loader />
  ) : (
    <div className={styles.requiredInformationContainer}>
      <Section
        title="Contractor Information"
        subSectionStates={[
          {
            isExist: true,
            isCompleted: contractorInformationComplete,
          },
        ]}
      >
        <FormProvider
          register={register}
          unregister={unregister}
          trigger={trigger}
          handleSubmit={handleSubmit}
          formState={formState}
          watch={watch}
          setValue={setValue}
          clearErrors={clearErrors}
          {...methods}
        >
          <form onSubmit={handleOnSubmit}>
            <div className={styles.radioButtonsHeader}>
              Have you located a contractor who will complete the work for your Home Improvement?
            </div>
            <div className={styles.radioButtons}>
              <RadioGroup
                value={watcher[ContractorInfoField.ContractorLocated]}
                onChange={(event) => {
                  const currentValue = convertStringToBoolean(event.target.value);

                  setValue(ContractorInfoField.ContractorLocated, currentValue);

                  setIsContractorInfoOpened(!!currentValue);

                  if (formState.isSubmitted) {
                    trigger();
                  }
                }}
              >
                <FormControlLabel
                  classes={{ label: styles.radioButtonLabel }}
                  value={true}
                  disabled={contractorInformationComplete}
                  control={
                    <Radio
                      classes={{
                        root: styles.radioButton,
                      }}
                    />
                  }
                  label={ContractorLocatedRadioValue.Yes}
                />
                <FormControlLabel
                  classes={{ label: styles.radioButtonLabel }}
                  value={false}
                  disabled={contractorInformationComplete}
                  control={
                    <Radio
                      classes={{
                        root: styles.radioButton,
                      }}
                    />
                  }
                  label={ContractorLocatedRadioValue.No}
                />
              </RadioGroup>
            </div>
            {!!formState.errors?.[ContractorInfoField.ContractorLocated] && (
              <FormHelperText className={styles.errorMessage}>This Information Is Required</FormHelperText>
            )}
            {isContractorInfoOpened && (
              <div>
                <div className={styles.formRow}>
                  <Input
                    label={ContractorInfoLabel.ContractorBusinessName}
                    errorMessage={formState.errors[ContractorInfoField.ContractorBusinessName]?.message}
                    className={styles.input}
                    name={ContractorInfoField.ContractorBusinessName}
                    onChange={(event) => {
                      setValue(ContractorInfoField.ContractorBusinessName, event.target.value);
                      trigger(ContractorInfoField.ContractorBusinessName);
                    }}
                    value={watcher[ContractorInfoField.ContractorBusinessName]}
                    disabled={contractorInformationComplete}
                  />
                </div>
                <div className={styles.formRow}>
                  <Input
                    label={ContractorInfoLabel.ContractorCity}
                    errorMessage={formState.errors[ContractorInfoField.ContractorCity]?.message}
                    className={styles.input}
                    name={ContractorInfoField.ContractorCity}
                    onChange={(event) => {
                      setValue(ContractorInfoField.ContractorCity, event.target.value);
                      trigger(ContractorInfoField.ContractorCity);
                    }}
                    value={watcher[ContractorInfoField.ContractorCity]}
                    disabled={contractorInformationComplete}
                  />
                  <Select
                    className={styles.input}
                    value={stateOptions.find((obj) => obj.value === watcher[ContractorInfoField.ContractorState])}
                    label={ContractorInfoLabel.ContractorState}
                    name={ContractorInfoField.ContractorState}
                    options={stateOptions}
                    inputContainerClassName={styles.input}
                    isSearchable
                    errorMessage={formState.errors[ContractorInfoField.ContractorState]?.message}
                    formatOptionLabel={formatOptionLabel}
                    onChange={(option) => {
                      setValue(ContractorInfoField.ContractorState, (option as OptionType).value);
                      trigger(ContractorInfoField.ContractorState);
                    }}
                    onInputChange={(value) => {
                      if (value) {
                        return value.length <= 2 ? value.replace(/[^A-Z,a-z]/g, '').slice(0, 2) : '';
                      }
                    }}
                    isDisabled={contractorInformationComplete}
                  />
                </div>
                <div className={styles.formRow}>
                  <Input
                    label={ContractorInfoLabel.ContractorFirstName}
                    errorMessage={formState.errors[ContractorInfoField.ContractorFirstName]?.message}
                    className={styles.input}
                    name={ContractorInfoField.ContractorFirstName}
                    onChange={(event) => {
                      setValue(ContractorInfoField.ContractorFirstName, event.target.value);
                      trigger(ContractorInfoField.ContractorFirstName);
                    }}
                    value={watcher[ContractorInfoField.ContractorFirstName]}
                    disabled={contractorInformationComplete}
                  />
                  <PhoneNumberInput
                    label={ContractorInfoLabel.ContractorPhone}
                    name={ContractorInfoField.ContractorPhone}
                    className={styles.input}
                    inputContainerClassname={styles.input}
                    value={watcher[ContractorInfoField.ContractorPhone]}
                    errorMessage={formState.errors[ContractorInfoField.ContractorPhone]?.message}
                    onChange={(value: string) => {
                      clearErrors(ContractorInfoField.ContractorPhone);
                      setValue(ContractorInfoField.ContractorPhone, value);
                    }}
                    onBlur={() => {
                      if (watcher[ContractorInfoField.ContractorPhone]) {
                        trigger(ContractorInfoField.ContractorPhone);
                      }
                    }}
                    disabled={contractorInformationComplete}
                  />
                </div>
              </div>
            )}
            {!contractorInformationComplete && <Button onClick={handleOnSubmit}>Save</Button>}
          </form>
        </FormProvider>
      </Section>
    </div>
  );
};

export default ContractorInformationTab;
